import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Autocomplete,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseButton from "@/components/buttons/CloseButton";
import { useEffect, useMemo, useState } from "react";
import { useSearchMasterPart } from "@/hooks/manualMasterPartHooks";
import { useGetFirstTierSuppliers } from "@/hooks/supplierHooks";
import type {
  ManualMasterPartSearchItem,
  ManualMasterPartPair,
} from "@/api/manualMasterPartService";

interface AddManualMasterPartModalProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (pairs: ManualMasterPartPair[]) => void;
  isSubmitting: boolean;
}

const getVariantLabel = (variant: ManualMasterPartSearchItem) => {
  const parts = [variant.part_number, variant.part_name, variant.ac, variant.rt].filter(
    Boolean,
  );
  return parts.join(" - ");
};

const AddManualMasterPartModal = ({
  open,
  handleClose,
  handleSubmit,
  isSubmitting,
}: AddManualMasterPartModalProps) => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedVariant, setSelectedVariant] =
    useState<ManualMasterPartSearchItem | null>(null);
  const [selectedSupplierCode, setSelectedSupplierCode] = useState<string | null>(
    null,
  );
  const [pairs, setPairs] = useState<ManualMasterPartPair[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // reset ฟอร์มทุกครั้งที่ modal ปิด ไม่ว่าจะปิดผ่านปุ่ม X, backdrop, หรือปิดจาก
  // parent เองหลัง submit สำเร็จ (component นี้ไม่ได้ unmount ตอน open=false เพราะ
  // parent render ไว้เฉยๆ แค่สลับ prop open)
  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setDebouncedSearch("");
      setSelectedVariant(null);
      setSelectedSupplierCode(null);
      setPairs([]);
    }
  }, [open]);

  const { data: searchResults = [], isFetching: isSearching } =
    useSearchMasterPart(debouncedSearch);

  const { data: suppliers = [] } = useGetFirstTierSuppliers({ skip: 0, limit: 2000 });

  const supplierOptions = useMemo(() => {
    if (!selectedVariant) return [];
    const alreadyAdded = new Set(
      pairs
        .filter(
          (p) =>
            p.part_number === selectedVariant.part_number &&
            (p.ac ?? "") === (selectedVariant.ac ?? "") &&
            (p.rt ?? "") === (selectedVariant.rt ?? ""),
        )
        .map((p) => p.supplier_code),
    );
    const existing = new Set(selectedVariant.existing_supplier_codes);
    return suppliers.filter(
      (s) => !alreadyAdded.has(s.supplier_code) && !existing.has(s.supplier_code),
    );
  }, [selectedVariant, suppliers, pairs]);

  const handleAddPair = () => {
    if (!selectedVariant || !selectedSupplierCode) return;
    setPairs((prev) => [
      ...prev,
      {
        part_number: selectedVariant.part_number,
        part_name: selectedVariant.part_name ?? "",
        ac: selectedVariant.ac,
        rt: selectedVariant.rt,
        supplier_code: selectedSupplierCode,
      },
    ]);
    setSelectedSupplierCode(null);
  };

  const handleRemovePair = (index: number) => {
    setPairs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitClick = () => {
    if (pairs.length === 0) return;
    handleSubmit(pairs);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxWidth: 700, maxHeight: "90vh" }}>
        <CloseButton onClick={handleClose} />
        <Typography variant="h2">เพิ่ม Master Part (Manual)</Typography>
        <Divider sx={{ my: 1 }} />
        <Box p={1} display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            fullWidth
            value={selectedVariant}
            inputValue={searchInput}
            onInputChange={(_event, newValue) => setSearchInput(newValue)}
            onChange={(_event, newValue) => setSelectedVariant(newValue)}
            options={searchResults}
            loading={isSearching}
            getOptionLabel={(option) => getVariantLabel(option)}
            isOptionEqualToValue={(option, value) =>
              option.part_number === value.part_number &&
              option.ac === value.ac &&
              option.rt === value.rt
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="ค้นหา Part Number (พิมพ์อย่างน้อย 3 ตัวอักษร)"
              />
            )}
          />

          {selectedVariant && (
            <Box display="flex" gap={2} alignItems="center">
              <Autocomplete
                fullWidth
                value={
                  suppliers.find((s) => s.supplier_code === selectedSupplierCode) ??
                  null
                }
                onChange={(_event, newValue) =>
                  setSelectedSupplierCode(newValue ? newValue.supplier_code : null)
                }
                options={supplierOptions}
                getOptionLabel={(option) =>
                  `${option.supplier_code}-${option.supplier_abbr}-${option.supplier_name}`
                }
                isOptionEqualToValue={(option, value) =>
                  option.supplier_code === value.supplier_code
                }
                renderInput={(params) => (
                  <TextField {...params} label="Supplier Code" />
                )}
              />
              <Button
                variant="outlined"
                sx={DefaultPrimaryButton}
                disabled={!selectedSupplierCode}
                onClick={handleAddPair}
              >
                เพิ่ม
              </Button>
            </Box>
          )}

          {pairs.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>AC</TableCell>
                    <TableCell>Routing</TableCell>
                    <TableCell>Supplier Code</TableCell>
                    <TableCell align="center">ลบ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pairs.map((pair, index) => (
                    <TableRow key={`${pair.part_number}-${pair.ac}-${pair.rt}-${pair.supplier_code}`}>
                      <TableCell>{pair.part_number}</TableCell>
                      <TableCell>{pair.ac || "-"}</TableCell>
                      <TableCell>{pair.rt || "-"}</TableCell>
                      <TableCell>{pair.supplier_code}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleRemovePair(index)}>
                          <DeleteIcon color="error" fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            disabled={pairs.length === 0 || isSubmitting}
            onClick={handleSubmitClick}
          >
            บันทึก ({pairs.length})
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddManualMasterPartModal;
